
import { Helmet } from "react-helmet";
import { MessageSquare, Brain, Code, Sparkles, Download, ExternalLink } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";

const WhatIsChatGPT = () => {
  const chromeExtensionUrl = "https://chromewebstore.google.com/detail/chat-gpt-for-chrome-with/fnmihdojmnkclgjpcoonokmkhjpjechg";
  const extensionReviewsUrl = "https://chromewebstore.google.com/detail/chat-gpt-for-chrome-with/fnmihdojmnkclgjpcoonokmkhjpjechg/reviews";

  return (
    <>
      <Helmet>
        <title>What is ChatGPT? | World of AI</title>
        <meta name="description" content="Learn about ChatGPT, OpenAI's advanced language model and how it's transforming the AI landscape." />
      </Helmet>

      <main className="min-h-screen pt-24 pb-16 bg-gradient-to-br from-blue-50 via-white to-indigo-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <header className="mb-12 text-center">
            <h1 className="text-4xl sm:text-5xl font-bold mb-6 text-gray-900">What is ChatGPT?</h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A comprehensive guide to OpenAI's revolutionary language model that's changing how we interact with AI.
            </p>
            
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button 
                size="lg" 
                className="bg-green-600 hover:bg-green-700 text-white font-medium rounded-full px-8 py-6 shadow-lg hover:shadow-xl transition-all flex items-center gap-2 w-full sm:w-auto"
                onClick={() => window.open(chromeExtensionUrl, "_blank")}
              >
                <Download className="h-5 w-5" />
                Install ChatGPT Chrome Extension
              </Button>
              <Button 
                variant="outline"
                size="lg"
                className="border-blue-500 text-blue-600 hover:bg-blue-50 font-medium rounded-full px-8 py-6 shadow-md hover:shadow-lg transition-all flex items-center gap-2 w-full sm:w-auto"
                onClick={() => window.open(extensionReviewsUrl, "_blank")}
              >
                <ExternalLink className="h-5 w-5" />
                Try ChatGPT Online
              </Button>
            </div>
          </header>

          <div className="mb-12">
            <img 
              src="https://images.unsplash.com/photo-1488590528505-98d2b5aba04b" 
              alt="AI Technology Concept" 
              className="w-full h-64 md:h-96 object-cover rounded-xl shadow-md mb-8"
            />
            
            <Alert className="mb-8 bg-blue-50 border-blue-200">
              <MessageSquare className="h-5 w-5 text-blue-600" />
              <AlertTitle className="text-blue-800">Revolutionary AI Technology</AlertTitle>
              <AlertDescription className="text-blue-700">
                ChatGPT represents a significant advancement in AI capabilities, offering human-like text generation and understanding.
              </AlertDescription>
            </Alert>

            <div className="prose prose-lg max-w-none text-gray-700">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Introduction to ChatGPT</h2>
              <p>
                ChatGPT is an advanced language model developed by OpenAI, designed to understand and generate human-like text based on the input it receives. 
                Launched in November 2022, it quickly became one of the fastest-growing consumer applications in history, reaching over 100 million users within 
                just two months of its release.
              </p>
              <p>
                Built on the GPT (Generative Pre-trained Transformer) architecture, ChatGPT represents a significant leap forward in natural language 
                processing technology. It can engage in conversations, answer questions, write essays, summarize texts, translate languages, and even 
                create creative content like poems, scripts, and stories.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">How ChatGPT Works</h2>
              <p>
                ChatGPT is built on a neural network machine learning model that has been trained on vast amounts of text data from the internet. 
                The model uses a technique called transformer architecture, which allows it to understand context and generate coherent responses.
              </p>
              <p>
                When you interact with ChatGPT, it processes your input, analyzes the context of your conversation, and generates a response based on patterns 
                it learned during training. This makes the conversation feel natural and human-like, although it doesn't actually "understand" the way humans do.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <Card className="bg-white/70 backdrop-blur-sm hover:shadow-lg transition-shadow">
              <CardHeader className="pb-2">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                  <Brain className="h-6 w-6 text-blue-600" />
                </div>
                <CardTitle>Advanced Intelligence</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">ChatGPT can understand context, remember previous parts of conversations, and provide relevant responses.</p>
              </CardContent>
            </Card>

            <Card className="bg-white/70 backdrop-blur-sm hover:shadow-lg transition-shadow">
              <CardHeader className="pb-2">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                  <Code className="h-6 w-6 text-blue-600" />
                </div>
                <CardTitle>Coding Assistant</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">Beyond conversation, ChatGPT can help with programming tasks by writing, explaining, and debugging code.</p>
              </CardContent>
            </Card>

            <Card className="bg-white/70 backdrop-blur-sm hover:shadow-lg transition-shadow">
              <CardHeader className="pb-2">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                  <Sparkles className="h-6 w-6 text-blue-600" />
                </div>
                <CardTitle>Creative Capabilities</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">Generate creative content, from poems and stories to marketing copy and business ideas.</p>
              </CardContent>
            </Card>
          </div>

          <div className="mb-12 bg-white/80 rounded-xl p-8 shadow-md">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">ChatGPT Versions and Capabilities</h2>
            <p className="mb-6 text-gray-700">
              OpenAI has released several versions of ChatGPT, each with improved capabilities:
            </p>

            <div className="space-y-4">
              <div className="border-l-4 border-blue-500 pl-4 py-2">
                <h3 className="font-semibold text-gray-900">GPT-3.5</h3>
                <p className="text-gray-600">The original ChatGPT model with strong conversational abilities and broad knowledge.</p>
              </div>
              
              <div className="border-l-4 border-blue-500 pl-4 py-2">
                <h3 className="font-semibold text-gray-900">GPT-4</h3>
                <p className="text-gray-600">More advanced with improved reasoning, creativity, and technical capabilities. It can handle more complex tasks and has a larger context window.</p>
              </div>
              
              <div className="border-l-4 border-blue-500 pl-4 py-2">
                <h3 className="font-semibold text-gray-900">GPT-4o</h3>
                <p className="text-gray-600">The latest multimodal model that can process text, images, audio, and video inputs for more versatile interactions.</p>
              </div>
            </div>
          </div>

          <div className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h2>
            <Accordion type="single" collapsible className="bg-white/80 rounded-xl p-6 shadow-md">
              <AccordionItem value="item-1">
                <AccordionTrigger className="text-lg font-medium">How does ChatGPT compare to DeepSeek AI?</AccordionTrigger>
                <AccordionContent className="text-gray-700">
                  While both are powerful language models, ChatGPT is developed by OpenAI and has been widely adopted globally. DeepSeek AI is a newer competitor 
                  from China that offers open-source alternatives with competitive performance at potentially lower costs. Each has different strengths in various 
                  tasks and use cases.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-2">
                <AccordionTrigger className="text-lg font-medium">Is ChatGPT free to use?</AccordionTrigger>
                <AccordionContent className="text-gray-700">
                  ChatGPT has both free and paid tiers. The free version provides access to GPT-3.5, while ChatGPT Plus 
                  ($20/month) offers access to GPT-4 and GPT-4o, faster response times, priority access during peak times, and access to newer features.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-3">
                <AccordionTrigger className="text-lg font-medium">What can ChatGPT be used for?</AccordionTrigger>
                <AccordionContent className="text-gray-700">
                  ChatGPT can be used for a wide range of tasks, including answering questions, writing and editing content, 
                  summarizing long texts, translating languages, generating creative content, coding assistance, brainstorming ideas, 
                  learning concepts, and much more.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-4">
                <AccordionTrigger className="text-lg font-medium">What are ChatGPT's limitations?</AccordionTrigger>
                <AccordionContent className="text-gray-700">
                  ChatGPT has several limitations: it can occasionally provide incorrect information (hallucinate), has limited knowledge of 
                  events after its training cutoff date, may not understand complex or ambiguous queries, doesn't have true understanding or 
                  consciousness, and may sometimes generate biased or inappropriate content despite safeguards.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>

          <div className="bg-blue-50 rounded-xl p-8 shadow-md mb-16">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">The Future of ChatGPT</h2>
            <p className="text-gray-700 mb-4">
              OpenAI continues to improve ChatGPT with regular updates and new capabilities. The future roadmap likely includes:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 mb-6">
              <li>Enhanced multimodal capabilities (text, image, audio, video)</li>
              <li>Improved reasoning and reduced hallucinations</li>
              <li>More specialized versions for different industries</li>
              <li>Better integration with third-party tools and services</li>
              <li>Expanded API capabilities for developers</li>
            </ul>
            <p className="text-gray-700">
              As AI technology advances, ChatGPT will likely become more capable, reliable, and integrated into our daily digital experiences.
            </p>
          </div>

          <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-xl p-8 shadow-lg text-white text-center">
            <h2 className="text-2xl font-bold mb-4">Enhance Your ChatGPT Experience</h2>
            <p className="text-lg mb-6">
              Get the most out of ChatGPT with our Chrome extension that brings AI assistance directly to your browser.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Button
                size="lg"
                className="bg-green-500 hover:bg-green-600 text-white font-medium rounded-full px-8 py-6 shadow-lg hover:shadow-xl transition-all flex items-center gap-2 w-full sm:w-auto"
                onClick={() => window.open(chromeExtensionUrl, "_blank")}
              >
                <Download className="h-5 w-5" />
                Download Chrome Extension
              </Button>
              <Button
                variant="outline"
                size="lg"
                className="bg-transparent border-2 border-white text-white hover:bg-white/10 font-medium rounded-full px-8 py-6 shadow-md hover:shadow-lg transition-all flex items-center gap-2 w-full sm:w-auto"
                onClick={() => window.open(extensionReviewsUrl, "_blank")}
              >
                <ExternalLink className="h-5 w-5" />
                Read User Reviews
              </Button>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default WhatIsChatGPT;
